/**
 * Desktop Notifications utility
 *
 * Uses the Web Notifications API to show native OS notifications
 * for new chat and mail messages, even when the browser tab is
 * minimized or in the background.
 */

import { isNativeApp, nativeNotify, nativeRequestNotificationPermission } from './nativeApp'

let permissionGranted = false

/**
 * Request notification permission from the user.
 * Should be called once after authentication.
 */
export function requestNotificationPermission() {
  // The Android app shows them as the phone's own notifications
  if (isNativeApp) {
    nativeRequestNotificationPermission()
    return
  }

  if (!('Notification' in window)) {
    console.warn('Desktop notifications are not supported in this browser.')
    return
  }

  if (Notification.permission === 'granted') {
    permissionGranted = true
    return
  }

  if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((permission) => {
      permissionGranted = permission === 'granted'
    })
  }
}

/**
 * Show a desktop notification.
 *
 * @param {string} title  - Notification title
 * @param {object} options - Notification options (body, icon, tag, etc.)
 */
export function showDesktopNotification(title, options = {}) {
  // In the Android app a tap opens options.url, the page to show
  if (isNativeApp) {
    nativeNotify({ tag: options.tag, title, body: options.body, url: options.url, channel: options.channel })
    return
  }

  if (!permissionGranted || !('Notification' in window)) {
    return
  }

  const defaults = {
    icon: '/favicon.ico',
  }

  // onClick, url and channel are ours, not the Notification API's: what to open when the notification is clicked
  const { onClick, url, channel, ...notificationOptions } = options
  const notification = new Notification(title, { ...defaults, ...notificationOptions })

  notification.onclick = () => {
    // Bring the browser tab to the foreground
    window.focus()
    notification.close()
    if (onClick) onClick()
  }

  // Auto-close after 8 seconds
  setTimeout(() => notification.close(), 8000)
}
